import { Button, Divider, IconButton, Popover, Stack, Typography } from '@chronosphereio/chrono-ui';
import { useMemo } from 'react';
import { getErrorMessageFromUnknown, Notice } from '@/core';
import { getIconFromNoticeType, getMostSevereNoticeType } from './model';

export interface VizContainerNoticesBaseProps {
  /**
   * Errors passed to the container. If errors are present, notices are ignored.
   */
  errors?: unknown[];
  /**
   * Notices passed to the container.
   */
  notices?: Notice[];
}

/**
 * Optional component used to render notices within `VizContainer.Header`.
 *
 * This is the "base" version of the component that comes with very minimal features and dependencies on context.
 * Instead use `Notices` if you want a more feature-rich root component.
 */
export const VizContainerNoticesBase = ({ errors, notices }: VizContainerNoticesBaseProps) => {
  const messages: Notice[] | undefined = useMemo(() => {
    if (errors?.length) {
      return errors.map((e) => ({ type: 'error', message: getErrorMessageFromUnknown(e) }));
    } else if (notices?.length) {
      return notices;
    }
  }, [errors, notices]);

  if (!messages || !messages.length) return null;

  return (
    <Popover
      title={messages.length > 1 ? `${messages.length} messages` : ''}
      Content={({ closePopover }) => (
        <Stack spacing={1} sx={{ maxWidth: '300px' }}>
          {messages.map((message, index) => {
            return (
              <Stack key={index} spacing={1}>
                {index !== 0 && <Divider sx={{ paddingTop: 0.5, marginBottom: 0.5 }} />}
                {messages.length > 1 ? (
                  <Stack direction="row" alignItems="start" spacing={0.5}>
                    {getIconFromNoticeType(message.type)}
                    <VizContainerNotice notice={message} closePopover={closePopover} />
                  </Stack>
                ) : (
                  <VizContainerNotice notice={message} closePopover={closePopover} />
                )}
              </Stack>
            );
          })}
        </Stack>
      )}
    >
      <IconButton size="small" data-testid="viz-container-icon">
        {getIconFromNoticeType(getMostSevereNoticeType(messages))}
      </IconButton>
    </Popover>
  );
};

interface VizContainerNoticeProps {
  notice: Notice;
  closePopover: () => void;
}

/**
 * Renders each individual notice within the panel notifications popover.
 */
const VizContainerNotice = ({ notice, closePopover }: VizContainerNoticeProps) => {
  return (
    <Stack spacing={1} sx={{ overflowWrap: 'break-word', minWidth: 0 }}>
      <Typography variant="body2">{getErrorMessageFromUnknown(notice)}</Typography>
      {notice.description && <Typography variant="body2">{notice.description}</Typography>}
      {notice.action && (
        <Button
          size="small"
          variant={notice.action.isDanger ? 'tertiary' : 'secondary'}
          danger={notice.action.isDanger}
          sx={{ alignSelf: 'flex-start' }}
          onClick={() => {
            notice.action?.onClick();
            closePopover();
          }}
        >
          {notice.action.label}
        </Button>
      )}
    </Stack>
  );
};
